import { Props as ButtonWebProps } from '@rover/kibble/core/Button/Button';
import { ButtonSizes } from '@rover/kibble/core/Button/Button.common';
import { Props as ButtonNativeProps } from '@rover/kibble/core/Button/Button.native';

export type ButtonWithCountBadgeCommonProps = {
  badgeCount?: number;
  ariaLabel?: string;
  badgeTestID?: string;
  /**
   * When true, hides the count badge from the accessibility tree (iOS
   * accessibilityElementsHidden + Android importantForAccessibility). Defaults to false.
   * No effect on web.
   *
   * Only set this true when the count is announced some other way (e.g. included in the
   * button's ariaLabel). Otherwise the count becomes silent to screen readers — verify
   * with VoiceOver/TalkBack before enabling.
   */
  hideBadgeFromScreenReader?: boolean;
};

export const BUTTON_SIZE_TO_BADGE_SIZE_MAP: Record<ButtonSizes, number> = {
  small: 16,
  default: 24,
  large: 24,
};

// Offset of a circle badge from the button's top-right corner, per Figma. Pills grow rightward from the same left edge.
export const BUTTON_SIZE_TO_BADGE_OFFSET_MAP: Record<ButtonSizes, { top: number; right: number }> =
  {
    small: { top: 0, right: -3 },
    default: { top: -4, right: -2 },
    large: { top: -4, right: -2 },
  };

// Width of the borderWidth.primary ring, for positioning math. Figma draws the ring outside the fill.
export const BADGE_RING_PX = 2;

// Averta sits digits ~0.0388em low when centered; this lifts them to optical center.
export const BADGE_DIGIT_OPTICAL_OFFSET = -0.5;

// Figma grows the badge into a pill at two digits on small and at three digits on the other sizes.
export const isPillBadge = (badgeCount: number | undefined, size: ButtonSizes): boolean =>
  String(badgeCount ?? '').length > (size === 'small' ? 1 : 2);

export type ButtonWithCountBadgeNativeProps = ButtonWithCountBadgeCommonProps & ButtonNativeProps;

export type ButtonWithCountBadgeWebProps = ButtonWithCountBadgeCommonProps & ButtonWebProps;
